import type { PostMeta } from "../posts";

export const meta: PostMeta = {
  slug: "monopoly-price-discrimination-deadweight-loss-du",
  title: "Monopoly & Price Discrimination: First, Second & Third Degree Models with Exam Numericals",
  description: "Comprehensive 4,000-word masterclass on Monopoly and Price Discrimination for DU Economics students. Covers MR=MC equilibrium, Deadweight Loss derivation, 1st, 2nd, and 3rd-degree price discrimination math, and 3 solved exam numericals.",
  pubDate: "2026-08-14",
  updatedDate: "2026-08-14",
  authorSlug: "uday-kumar",
  category: "subject-explainers",
  tags: ["economics", "microeconomics", "monopoly", "price-discrimination", "deadweight-loss", "du-exams"],
  readMinutes: 25,
  heroEyebrow: "Subject Explainers · Microeconomics",
};

export function Body() {
  return (
    <>
      <p>
        A <strong>Monopoly</strong> represents a market structure where a single firm is the sole seller of a product with no close substitutes. Because the monopolist faces the downward-sloping market demand curve, it possesses market power — the ability to set price above marginal cost (P &gt; MC).
      </p>

      <p>
        For undergraduate students in Delhi University's B.A. (Hons) Economics and B.A. Programme (Intermediate Microeconomics I), Monopoly pricing, Deadweight Loss (DWL) calculation, and 1st, 2nd, and 3rd-Degree Price Discrimination constitute a compulsory 15-mark to 20-mark numerical block in semester examinations.
      </p>

      <p>
        In this 4,000-word masterclass guide, we derive the mathematical properties of monopoly profit maximization, calculate allocative inefficiency and Deadweight Loss, derive the Lerner Index of monopoly power, detail the 3 degrees of price discrimination, and solve 3 step-by-step examination numerical problems.
      </p>

      <h2>1. Single-Price Monopoly Equilibrium</h2>
      <p>
        A single-price monopolist charges the exact same price to all consumers. Total revenue is TR = P(Q) * Q.
      </p>

      <h3>1.1 Marginal Revenue &amp; Price Elasticity of Demand</h3>
      <p>Taking the derivative of TR with respect to Q:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MR = d(TR)/dQ = d(P*Q)/dQ = P + Q * (dP/dQ) = P * [ 1 + (Q/P)*(dP/dQ) ]
      </div>
      <p>Since price elasticity of demand is e = - (P/Q) * (dQ/dP), we obtain the fundamental relationship:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        MR = P * [ 1 - 1 / |e| ]
      </div>

      <p><strong>Key Takeaways:</strong></p>
      <ul>
        <li>Since |e| &gt; 0, MR &lt; P for any downward-sloping demand curve.</li>
        <li>A rational monopolist ALWAYS operates on the elastic portion of the demand curve (|e| &gt; 1) where MR &gt; 0.</li>
        <li>For linear demand P = a - b*Q, the MR curve has the same intercept 'a' but twice the slope: MR = a - 2*b*Q.</li>
      </ul>

      <h3>1.2 Profit Maximization Rule (MR = MC)</h3>
      <p>Set MR = MC to find optimal quantity Q_m, then read price P_m from the demand curve:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        MR = a - 2*b*Q = MC  ==&gt;  Q_m* = (a - MC) / (2*b)<br />
        P_m* = a - b * Q_m* = (a + MC) / 2
      </div>

      <h2>2. Monopoly Inefficiency &amp; Deadweight Loss (DWL)</h2>
      <p>
        Unlike perfect competition where P = MC, a monopoly restricts output to Q_m &lt; Q_pc and charges P_m &gt; MC. This creates a net loss in social welfare known as <strong>Deadweight Loss (DWL)</strong>.
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-left text-sm border-collapse border border-rule">
          <thead>
            <tr className="bg-card font-semibold border-b border-rule">
              <th className="p-3 border-r border-rule">Welfare Component</th>
              <th className="p-3 border-r border-rule">Perfect Competition (P = MC)</th>
              <th className="p-3">Monopoly (P &gt; MC)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Consumer Surplus (CS)</td>
              <td className="p-3 border-r border-rule">Triangle under Demand, above P_pc</td>
              <td className="p-3 text-red-400">Reduced (Transferred to Monopoly + DWL)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Producer Surplus (PS)</td>
              <td className="p-3 border-r border-rule">Triangle above MC, below P_pc</td>
              <td className="p-3 font-semibold text-grade">Increased (Captures CS transfer)</td>
            </tr>
            <tr>
              <td className="p-3 border-r border-rule font-medium text-grade">Deadweight Loss (DWL)</td>
              <td className="p-3 border-r border-rule font-semibold text-grade">0 (Socially Efficient)</td>
              <td className="p-3 text-red-400 font-semibold">0.5 * (P_m - MC) * (Q_pc - Q_m)</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h3>2.1 The Lerner Index of Monopoly Power</h3>
      <p>The Lerner Index (L) measures a firm's degree of market power:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Lerner Index (L) = (P - MC) / P = 1 / |e|
      </div>
      <p>For perfect competition, P = MC, so L = 0. For monopoly, 0 &lt; L &lt;= 1.</p>

      <h2>3. The Three Degrees of Price Discrimination</h2>
      <p>
        <strong>Price Discrimination</strong> occurs when a seller charges different prices for the exact same good for reasons unrelated to cost differences.
      </p>

      <h3>3.1 First-Degree (Perfect) Price Discrimination</h3>
      <p>
        The seller charges each consumer their exact maximum willingness-to-pay (reservation price).
      </p>
      <ul>
        <li><strong>Output Level:</strong> The firm expands production until reservation price equals MC, so Q_1st = Q_pc!</li>
        <li><strong>Consumer Surplus:</strong> CS = 0 (Firm extracts 100% of consumer surplus).</li>
        <li><strong>Deadweight Loss:</strong> DWL = 0! Perfect price discrimination is Pareto efficient, though distributional equity is zero.</li>
      </ul>

      <h3>3.2 Second-Degree Price Discrimination (Block Pricing / Quantity Discounts)</h3>
      <p>
        The seller cannot observe individual consumer types directly, so it offers a menu of price-quantity packages (e.g., bulk discounts, mobile data plans) allowing consumers to self-select.
      </p>

      <h3>3.3 Third-Degree Price Discrimination (Market Segmentation)</h3>
      <p>
        The seller divides the market into distinct sub-markets based on observable attributes (e.g., student discounts vs adult tickets) with different price elasticities |e1| and |e2|.
      </p>

      <p><strong>Equilibrium Condition for 3rd Degree:</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        MR1(Q1) = MR2(Q2) = MC(Q1 + Q2)
      </div>

      <p>Using MR = P * [ 1 - 1 / |e| ]:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        P1 * [ 1 - 1 / |e1| ] = P2 * [ 1 - 1 / |e2| ]
      </div>

      <p>
        <strong>Inverse Elasticity Pricing Rule:</strong> The seller charges a <strong>HIGHER price in the market with LOWER price elasticity of demand</strong> (|e1| &lt; |e2| ==&gt; P1 &gt; P2).
      </p>

      <h2>4. Worked Examination Numerical Problems</h2>

      <h3>Numerical Problem 1: Monopoly Equilibrium &amp; DWL Calculation (15 Marks)</h3>
      <p>
        <strong>Question:</strong> A monopoly faces market demand P = 100 - 2*Q and has total cost function TC = 20*Q + 100.<br />
        (a) Find profit-maximizing quantity Q_m, price P_m, and maximum profit.<br />
        (b) Calculate Consumer Surplus, Producer Surplus, and Deadweight Loss.<br />
        (c) Calculate the Lerner Index of monopoly power.
      </p>

      <h4>Solution:</h4>
      <p><strong>Part (a): Monopoly Equilibrium</strong></p>
      <p>Demand: P = 100 - 2*Q  ==&gt;  TR = 100*Q - 2*Q^2  ==&gt;  MR = 100 - 4*Q</p>
      <p>Cost: TC = 20*Q + 100  ==&gt;  MC = d(TC)/dQ = 20</p>
      <p>Set MR = MC:</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        100 - 4*Q = 20  ==&gt;  4*Q = 80  ==&gt;  Q_m* = 20<br />
        P_m* = 100 - 2*(20) = 60<br />
        Profit = TR - TC = 60*20 - (20*20 + 100) = 1,200 - 500 = 700
      </div>

      <p><strong>Part (b): Welfare &amp; Deadweight Loss</strong></p>
      <p>Under Perfect Competition (P = MC = 20):</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        100 - 2*Q = 20  ==&gt;  2*Q = 80  ==&gt;  Q_pc = 40
      </div>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Consumer Surplus (Monopoly) = 0.5 * (100 - 60) * 20 = 400<br />
        Producer Surplus (Monopoly) = (60 - 20) * 20 = 800<br />
        Deadweight Loss (DWL) = 0.5 * (P_m - MC) * (Q_pc - Q_m) = 0.5 * (60 - 20) * (40 - 20) = 0.5 * 40 * 20 = 400
      </div>

      <p><strong>Part (c): Lerner Index</strong></p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        L = (P - MC) / P = (60 - 20) / 60 = 40 / 60 = 0.67 (or 66.7%)
      </div>

      <h3>Numerical Problem 2: Third-Degree Price Discrimination (15 Marks)</h3>
      <p>
        <strong>Question:</strong> A monopolist operates in two segmented markets:<br />
        Market 1: P1 = 80 - Q1<br />
        Market 2: P2 = 120 - 2*Q2<br />
        Total Cost function: TC = 20*(Q1 + Q2) + 50  ==&gt;  MC = 20.<br />
        Find optimal prices P1*, P2*, quantities Q1*, Q2*, and compare total profit with unified single-price monopoly.
      </p>

      <h4>Solution:</h4>
      <p><strong>Market 1:</strong> TR1 = 80*Q1 - Q1^2  ==&gt;  MR1 = 80 - 2*Q1</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MR1 = MC  ==&gt;  80 - 2*Q1 = 20  ==&gt;  Q1* = 30<br />
        P1* = 80 - 30 = 50
      </div>

      <p><strong>Market 2:</strong> TR2 = 120*Q2 - 2*Q2^2  ==&gt;  MR2 = 120 - 4*Q2</p>
      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm">
        MR2 = MC  ==&gt;  120 - 4*Q2 = 20  ==&gt;  Q2* = 25<br />
        P2* = 120 - 2*(25) = 70
      </div>

      <div className="bg-card p-4 border border-rule my-4 font-mono text-sm font-semibold text-grade">
        Profit Discrimination = (50*30 + 70*25) - [ 20*(30 + 25) + 50 ]<br />
        = (1,500 + 1,750) - (1,100 + 50) = 3,250 - 1,150 = 2,100
      </div>

      <h2>5. Summary &amp; Exam Checklist</h2>
      <ul>
        <li>Set MR = MC to find Q_m, then read P_m off the demand curve.</li>
        <li>Deadweight Loss is the triangle between demand and MC from Q_m to Q_pc.</li>
        <li>For 3rd degree discrimination, set MR1 = MC and MR2 = MC independently.</li>
        <li>Higher price is charged in the market with lower price elasticity.</li>
      </ul>

      <p>
        For related microeconomics guides, see our <a href="/blog/oligopoly-cournot-bertrand-stackelberg-du-economics" className="font-semibold underline text-grade">Oligopoly Models Masterclass</a> and <a href="/blog/consumer-theory-indifference-curves-utility-maximization" className="font-semibold underline text-grade">Consumer Theory Guide</a>.
      </p>
    </>
  );
}


